/**
 * Eine Kategorie der Navigation: Gruppentitel und darunter ihre Ansichten
 * (REQ-20 §3.1/§3.2).
 *
 * Der Gruppentitel ist ein `<button>` mit `aria-expanded` und `aria-controls`;
 * die Liste trägt die zugehörige `id`. Zugeklappt bleibt die Liste im DOM, ihre
 * Einträge werden aber nicht gerendert — so sind sie weder sichtbar noch mit
 * dem Tabulator erreichbar.
 */

import type { ReactElement } from 'react';

import type { ChinaAppSysGroupCategory } from '@/types/entities';
import { buildViewHref } from '@/utils/viewNavigation';

import { NavItem } from './NavItem';
import { groupChevronIcon, navIconFor } from './navIcons';

export interface NavGroupProps {
  category: ChinaAppSysGroupCategory;
  expanded: boolean;
  onToggle: () => void;
  /** Pfad der gerade offenen Route — markiert den aktiven Eintrag. */
  currentPath: string;
}

export function NavGroup({
  category,
  expanded,
  onToggle,
  currentPath,
}: NavGroupProps): ReactElement {
  const listId = `iwm-nav-group-${String(category.Id)}`;

  return (
    <div className="iwm-nav-group-block">
      <button
        type="button"
        className="iwm-nav-group"
        aria-expanded={expanded}
        aria-controls={listId}
        onClick={onToggle}
      >
        <span className="iwm-nav-group-label">{category.Bezeichnung}</span>
        <span className="iwm-nav-group-chevron">{groupChevronIcon({ size: 14 })}</span>
      </button>

      <div id={listId} className="iwm-nav-group-items">
        {expanded
          ? (category.Views ?? []).map((view) => {
              const href = buildViewHref(view);
              const name = view.ViewName ?? '';

              return (
                <NavItem
                  key={view.Id}
                  icon={navIconFor(view.Entity)}
                  label={name}
                  to={href === '' ? undefined : href}
                  disabled={href === ''}
                  active={href !== '' && href === currentPath}
                />
              );
            })
          : null}
      </div>
    </div>
  );
}

export default NavGroup;
